/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { create } from 'zustand';
import type { ActiveClaimDto, ClaimBatchDto } from '@shared/types';
import { claimFormulas, releaseClaim, fetchActiveClaims, sendHeartbeat, fetchMyClaim } from '../services/formularyApi';
import { trackEvent } from '../services/telemetry';

interface ClaimState {
  /** Current user's active claim batch (null if none) */
  myBatch: ClaimBatchDto | null;
  /** All active claims (for multi-user awareness) */
  allClaims: ActiveClaimDto[];
  /** Whether a claim operation is in-flight */
  isClaiming: boolean;
  /** Error from last claim/release operation */
  error: string | null;
  /** Heartbeat interval ID */
  heartbeatInterval: ReturnType<typeof setInterval> | null;

  claim: (count: number) => Promise<void>;
  release: () => Promise<void>;
  loadClaims: () => Promise<void>;
  startHeartbeat: () => void;
  stopHeartbeat: () => void;
  /** Called when SSE `claims-changed` fires */
  onClaimsChanged: () => void;
}

export const useClaimStore = create<ClaimState>((set, get) => ({
  myBatch: null,
  allClaims: [],
  isClaiming: false,
  error: null,
  heartbeatInterval: null,

  claim: async (count: number) => {
    set({ isClaiming: true, error: null });
    try {
      const response = await claimFormulas(count);
      const batch = response.data;
      if (batch.formulaSetIds.length === 0) {
        set({ isClaiming: false, error: 'No unclaimed formulas available' });
        return;
      }
      set({ myBatch: batch, isClaiming: false });
      trackEvent('formula_claim_started');
      get().startHeartbeat();
    } catch (err) {
      set({ isClaiming: false, error: (err as Error).message });
    }
  },

  release: async () => {
    const { myBatch } = get();
    if (!myBatch) return;
    try {
      await releaseClaim(myBatch.batchId);
      set({ myBatch: null, error: null });
      trackEvent('formula_claim_released', { releaseType: 'manual' });
      get().stopHeartbeat();
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  loadClaims: async () => {
    try {
      const [allResponse, myResponse] = await Promise.all([fetchActiveClaims(), fetchMyClaim()]);
      set({ allClaims: allResponse.data });

      if (myResponse.data) {
        if (!get().myBatch) {
          set({ myBatch: myResponse.data });
          get().startHeartbeat();
        }
      } else if (get().myBatch) {
        // Claim expired server-side
        set({ myBatch: null });
        get().stopHeartbeat();
      }
    } catch {
      // Best-effort
    }
  },

  startHeartbeat: () => {
    const existing = get().heartbeatInterval;
    if (existing) return; // Already running
    const interval = setInterval(() => {
      sendHeartbeat().catch(() => { /* best effort */ });
    }, 30_000); // Every 30 seconds
    set({ heartbeatInterval: interval });
    // Send initial heartbeat immediately
    sendHeartbeat().catch(() => {});
  },

  stopHeartbeat: () => {
    const { heartbeatInterval } = get();
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      set({ heartbeatInterval: null });
    }
  },

  onClaimsChanged: () => {
    const hadBatch = !!get().myBatch;
    void get().loadClaims().then(() => {
      if (hadBatch && !get().myBatch) {
        trackEvent('formula_claim_released', { releaseType: 'expired' });
      }
    });
  },
}));
